import { Navigate, useNavigate, useParams } from 'react-router-dom';
import type { RangeKind } from '@/data/catalogue';
import { useSite, useSiteLoading } from '@/services/siteData';
import { ProductCard } from '@/components/ProductCard';
import { Seo, breadcrumbLd } from '@/components/Seo';

export function RangePage({ kind }: { kind: RangeKind }) {
  const { slug = '' } = useParams();
  const navigate = useNavigate();
  const { getRange, getRanges, getProductsForRange } = useSite();
  const loading = useSiteLoading();

  // The Innovation range has its own dedicated page (not an order range here).
  if (kind === 'order' && slug === 'innovation') return <Navigate to="/innovation" replace />;

  const range = getRange(kind, slug);

  // Wait for the live catalogue before bouncing a direct deep-link to home.
  if (!range && loading) {
    return (
      <div className="page-fade">
        <div className="page-head">
          <p className="lede">Loading range…</p>
        </div>
      </div>
    );
  }

  if (!range) return <Navigate to="/" replace />;

  const products = getProductsForRange(range.key);
  const isPlaceholder = products.length === 0;
  const tabs = getRanges(kind);

  const path = `/${kind}/${slug}`;
  const seoTitle = `Wholesale ${range.label} — Trade Patisserie Supplier`;
  const seoDesc = `${range.label} patisserie for wholesale and food service. Trade accounts only — supplied to restaurants, hotels and caterers across the UK.`;

  return (
    <div className="page-fade">
      <Seo
        title={seoTitle}
        description={seoDesc}
        path={path}
        jsonLd={[
          breadcrumbLd([
            { name: 'Home', path: '/' },
            { name: range.label, path },
          ]),
        ]}
      />
      <div className="page-head">
        <div className="eyebrow">{kind === 'order' ? 'Available to Order' : 'Portfolio'}</div>
        <h1>{range.label}</h1>
        {range.lede && <p className="lede">{range.lede}</p>}
        {range.sub && <p className="sub">{range.sub}</p>}
      </div>

      <div className="tabs">
        {tabs.map((t) => (
          <button
            key={t.key}
            className={'tab' + (t.slug === slug ? ' active' : '')}
            onClick={() => navigate(`/${kind}/${t.slug}`)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {isPlaceholder ? (
        <div className="grid">
          {Array.from({ length: 8 }).map((_, n) => (
            <div className="card" key={n} style={{ pointerEvents: 'none' }}>
              <div className="card-img" style={{ display: 'grid', placeItems: 'center' }}>
                <span style={{ fontFamily: 'var(--font-serif)', color: 'var(--ink-faint)', fontStyle: 'italic', fontSize: 15 }}>
                  From the catalogue
                </span>
              </div>
              <div className="card-name" style={{ color: 'var(--ink-faint)' }}>
                {range.label} #{n + 1}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid">
          {products.map((p) => (
            <ProductCard key={p.code} product={p} />
          ))}
        </div>
      )}

      {isPlaceholder && (
        <p
          style={{
            textAlign: 'center',
            color: 'var(--ink-faint)',
            fontStyle: 'italic',
            fontFamily: 'var(--font-serif)',
            fontSize: 16,
            margin: '-20px auto 40px',
            maxWidth: 560,
          }}
        >
          This range will be populated from the product catalogue. (Live images weren't captured for this category yet.)
        </p>
      )}
    </div>
  );
}
